/* ds-wishlist.js — избранное без стороннего приложения.
 *
 * Хранилище — слой с одним интерфейсом (list/has/toggle) поверх localStorage.
 * Для залогиненных поверх работает remote-sync: theme.liquid рендерит блок
 * [data-ds-wishlist-sync] (customerId, hmac-token, серверный список из
 * customer metafield custom.wishlist), драйвер сливает его с локальным
 * состоянием и шлёт полный список POST-ом на amitea-script (debounce).
 * `snapshot` хранит список на момент последней успешной синхронизации —
 * по нему считаются локальные добавления и удаления, чтобы merge не
 * «воскрешал» удалённое на другом устройстве. Бэкенд недоступен — запись
 * молча откладывается до следующей загрузки, локальный режим не страдает.
 *
 * DOM-контракт (см. AGENTS.md — JS только через data-атрибуты):
 *   [data-ds-wishlist-toggle]  кнопка-сердечко; data-product-id, data-product-handle,
 *                              data-label-add/-remove, data-announce-add/-remove
 *   [data-ds-wishlist-count]   бейдж-счётчик в шапке; hidden при нуле
 * Событие `ds:wishlist:change` (document, detail: {items, count}) — для
 * страницы избранного и любых будущих подписчиков.
 */
(function () {
  var STORAGE_KEY = 'ds:wishlist:v1';
  var MAX_ITEMS = 100;

  function readState() {
    var fallback = { items: [], snapshot: [], updatedAt: 0 };
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return fallback;
      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.items)) return fallback;
      return {
        items: parsed.items.filter(function (item) {
          return item && item.id && item.handle;
        }),
        snapshot: Array.isArray(parsed.snapshot) ? parsed.snapshot : [],
        updatedAt: parsed.updatedAt || 0
      };
    } catch (error) {
      return fallback;
    }
  }

  function writeState(state) {
    state.updatedAt = Date.now();
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      /* Safari private mode и переполненный storage: избранное живёт до
         перезагрузки страницы, кнопки продолжают работать. */
    }
  }

  var state = readState();

  function findIndex(id) {
    var key = String(id);
    for (var i = 0; i < state.items.length; i += 1) {
      if (String(state.items[i].id) === key) return i;
    }
    return -1;
  }

  function has(id) {
    return findIndex(id) !== -1;
  }

  function list() {
    return state.items.slice();
  }

  function count() {
    return state.items.length;
  }

  function emitChange() {
    document.dispatchEvent(
      new CustomEvent('ds:wishlist:change', {
        detail: { items: list(), count: count() }
      })
    );
  }

  function toggle(id, handle, type) {
    var index = findIndex(id);
    if (index === -1) {
      if (state.items.length >= MAX_ITEMS) state.items.pop();
      var entry = { id: String(id), handle: String(handle) };
      if (type) entry.type = String(type);
      state.items.unshift(entry);
    } else {
      state.items.splice(index, 1);
    }
    writeState(state);
    syncAll();
    emitChange();
    schedulePush();
    return index === -1;
  }

  function add(id, handle) {
    if (!id || !handle || has(id)) return;
    toggle(id, handle);
  }

  function remove(id) {
    var index = findIndex(id);
    if (index === -1) return;
    state.items.splice(index, 1);
    writeState(state);
    syncAll();
    emitChange();
    schedulePush();
  }

  /* ---------- Remote sync (залогиненный покупатель) ---------- */

  var remote = null;
  var pushTimer = null;
  var pushing = false;
  var pushAgain = false;

  function idsOf(items) {
    return items.map(function (item) { return String(item.id); });
  }

  function schedulePush() {
    if (!remote) return;
    window.clearTimeout(pushTimer);
    pushTimer = window.setTimeout(push, 1500);
  }

  function push() {
    if (!remote) return;
    if (pushing) {
      pushAgain = true;
      return;
    }
    pushing = true;
    var sent = list();
    /* Товары и подборки уходят раздельно: товары — числовые id в `items`
       (metafield custom.wishlist), подборки — id метаобъектов в `sets`
       (custom.wishlist_sets). Нечисловой мусор отфильтровывается, иначе
       бэкенд отклонит весь push (400). */
    var productIds = [];
    var setIds = [];
    sent.forEach(function (item) {
      var id = String(item.id);
      if (item.type === 'set' || id.indexOf('set:') === 0) {
        var setId = id.replace('set:', '');
        if (/^\d{1,20}$/.test(setId)) setIds.push(setId);
      } else if (/^\d{1,20}$/.test(id)) {
        productIds.push(id);
      }
    });
    window
      .fetch(remote.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: remote.customerId,
          token: remote.token,
          items: productIds,
          sets: setIds
        })
      })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        state.snapshot = idsOf(sent);
        writeState(state);
      })
      .catch(function () {
        /* Сервер недоступен/отказал: snapshot не двигаем — расхождение
           допушится при следующей загрузке страницы. */
      })
      .then(function () {
        pushing = false;
        if (pushAgain) {
          pushAgain = false;
          schedulePush();
        }
      });
  }

  /* Слияние с серверным списком: серверное состояние — база, поверх неё
     локальные изменения с момента последнего снапшота. Удаление на этом
     устройстве не «воскресает» из серверного списка, а добавления с других
     устройств не теряются. */
  function reconcile(serverItems) {
    var snapshot = state.snapshot || [];
    var localIds = {};
    idsOf(state.items).forEach(function (id) { localIds[id] = true; });
    var snapshotIds = {};
    snapshot.forEach(function (id) { snapshotIds[id] = true; });

    var removedLocally = {};
    snapshot.forEach(function (id) {
      if (!localIds[id]) removedLocally[id] = true;
    });
    var addedLocally = state.items.filter(function (item) {
      return !snapshotIds[String(item.id)];
    });

    var merged = [];
    var seen = {};
    function mergedEntry(item) {
      var entry = { id: String(item.id), handle: String(item.handle) };
      if (item.type) entry.type = String(item.type);
      return entry;
    }
    addedLocally.forEach(function (item) {
      var id = String(item.id);
      if (seen[id]) return;
      seen[id] = true;
      merged.push(mergedEntry(item));
    });
    serverItems.forEach(function (item) {
      var id = String(item.id);
      if (seen[id] || removedLocally[id]) return;
      seen[id] = true;
      merged.push(mergedEntry(item));
    });
    if (merged.length > MAX_ITEMS) merged.length = MAX_ITEMS;

    var mergedIds = idsOf(merged).join(',');
    var serverIds = idsOf(serverItems).join(',');
    var changedLocally = mergedIds !== idsOf(state.items).join(',');

    state.items = merged;
    if (mergedIds === serverIds) {
      state.snapshot = idsOf(merged);
    }
    writeState(state);

    if (changedLocally) {
      syncAll();
      emitChange();
    }
    if (mergedIds !== serverIds) push();
  }

  function initRemote() {
    var el = document.querySelector('[data-ds-wishlist-sync]');
    if (!el || remote) return;
    try {
      var data = JSON.parse(el.textContent);
      if (!data || !data.customerId || !data.token || !data.endpoint) return;
      remote = data;
      reconcile(Array.isArray(data.items) ? data.items : []);
    } catch (error) {
      remote = null;
    }
  }

  /* ---------- DOM ---------- */

  function syncButton(button) {
    var active = has(button.getAttribute('data-product-id'));
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    var label = active
      ? button.getAttribute('data-label-remove')
      : button.getAttribute('data-label-add');
    if (label) button.setAttribute('aria-label', label);
  }

  function syncCounters() {
    var total = count();
    document.querySelectorAll('[data-ds-wishlist-count]').forEach(function (badge) {
      badge.textContent = total > 99 ? '99+' : String(total);
      if (total > 0) {
        badge.removeAttribute('hidden');
      } else {
        badge.setAttribute('hidden', '');
      }
    });
  }

  function syncAll(scope) {
    (scope || document)
      .querySelectorAll('[data-ds-wishlist-toggle]')
      .forEach(syncButton);
    syncCounters();
  }

  // Shared notice accepts plain strings; no HTML from callers.
  function showToast(message, added, options) {
    options = options || {};
    if (window.DsNotice) window.DsNotice.show({ title: options.title || message,
      description: options.description || options.detail || '', email: options.email,
      icon: options.icon || 'heart', added: added, actionLabel: options.actionLabel, onAction: options.onAction });
  }

  function onToggleClick(event) {
    var button = event.target.closest('[data-ds-wishlist-toggle]');
    if (!button) return;
    event.preventDefault();
    var added = toggle(
      button.getAttribute('data-product-id'),
      button.getAttribute('data-product-handle'),
      button.getAttribute('data-wl-type')
    );
    var message = added
      ? button.getAttribute('data-announce-add')
      : button.getAttribute('data-announce-remove');
    showToast(message, added);
    pop(button);
  }

  /* Микроанимация сердечка на клик (ds-wishlist.css: .is-popping) —
     перезапуск через reflow, чтобы быстрые повторные клики тоже прыгали. */
  function pop(button) {
    button.classList.remove('is-popping');
    void button.offsetWidth;
    button.classList.add('is-popping');
    button.addEventListener(
      'animationend',
      function () {
        button.classList.remove('is-popping');
      },
      { once: true }
    );
  }

  function init(scope) {
    initRemote();
    syncAll(scope || document);
  }

  document.addEventListener('click', onToggleClick);

  /* Другая вкладка изменила избранное — перечитываем и перерисовываем. */
  window.addEventListener('storage', function (event) {
    if (event.key !== STORAGE_KEY) return;
    state = readState();
    syncAll();
    emitChange();
  });

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  window.DsWishlist = {
    has: has,
    add: add,
    notify: showToast,
    dismissNotice: function () { if (window.DsNotice) window.DsNotice.hide(true); },
    toggle: toggle,
    remove: remove,
    list: list,
    count: count,
    init: init
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    }, { once: true });
  } else {
    init(document);
  }
})();
